import { ChangeDetectorRef, Component, inject, OnInit, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { RouterLink } from '@angular/router';
import { ApiSettings, LocalStorageKeyConstants } from '../../../../core/constants/constants';
import { ListConstants } from '../../../../core/constants/lists.constants';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { ExpenseListTableComponent } from '../../components/expense-list-table/expense-list-table.component';
import { ExpenseSearchFilterComponent } from '../../components/expense-search-filter/expense-search-filter.component';
import { SortDropdownComponent } from '../../../../parts/sort-dropdown/sort-dropdown/sort-dropdown';
import { IfHasPermissionDirective } from '../../../../core/directives/if-has-permission/if-has-permission.directive';
import { DataEntityTypes } from '../../../../core/enums/data.enums';
import { FinancialsPermission } from '../../../../core/enums/permissions.enums';
import { PageTitleComponent } from '../../../../parts/page-title/page-title.component';

@Component({
  selector: 'ms-expense-list-page',
  imports: [
    PageTitleComponent,
    CommonModule,
    FormsModule,
    FontAwesomeModule,
    RouterLink,
    ExpenseListTableComponent,
    ExpenseSearchFilterComponent,
    SortDropdownComponent,
    IfHasPermissionDirective
  ],
  templateUrl: './expense-list.page.html',
  styleUrl: './expense-list.page.scss'
})
export class ExpenseListPage implements OnInit {

  public sortOptions = ListConstants.EXPENSES_SORT_OPTIONS();
  public orderBy = signal<string>(ListConstants.EXPENSES_ORDERBY_DEFAULT());

  public FinancialsPermission = FinancialsPermission;
  public DataEntityTypes = DataEntityTypes;

  @ViewChild('expenseTable') public expenseTable?: ExpenseListTableComponent;

  public userSessionService = inject(UserSessionService);
  private changeDetector = inject(ChangeDetectorRef);

  public async ngOnInit(): Promise<void> {
    await this.scopeFilterToActiveOrganization();
    this.orderBy.set(this.userSessionService.expensesOrderBy);
  }

  public onSortChanged(orderBy: string): void {
    if (!orderBy || orderBy === this.orderBy()) return;
    this.orderBy.set(orderBy);
    this.userSessionService.expensesOrderBy = orderBy;
    this.userSessionService.saveToLocalStorage(
      LocalStorageKeyConstants.EXPENSES_ORDERBY(),
      orderBy
    );
    this.expenseTable?.setSortOrder(orderBy);
  }

  public onListOrderByChanged(orderBy: string): void {
    if (!orderBy || orderBy === this.orderBy()) return;
    this.orderBy.set(orderBy);
    this.userSessionService.expensesOrderBy = orderBy;
    this.userSessionService.saveToLocalStorage(
      LocalStorageKeyConstants.EXPENSES_ORDERBY(),
      orderBy
    );
  }

  public async onFilterApplied(): Promise<void> {
    this.persistFilter();
    await this.expenseTable?.loadExpenses(false);
    this.changeDetector.detectChanges();
  }

  private async scopeFilterToActiveOrganization(): Promise<void> {
    const activeOrg = this.userSessionService.activeOrganization();
    if (activeOrg) {
      this.userSessionService.expensesFilter.organizationIds = [activeOrg.organizationId];
    }
    const orgSettings = await this.userSessionService.getOrganizationSettings();
    this.userSessionService.expensesFilter.timeZoneId =
      orgSettings?.timeZoneId ?? ApiSettings.DEFAULT_ORG_TIMEZONE_FALLBACK().id;
  }

  private persistFilter(): void {
    this.userSessionService.saveToLocalStorage(
      LocalStorageKeyConstants.EXPENSES_FILTER(),
      this.userSessionService.expensesFilter
    );
  }
}
